import * as Yup from "yup";
import {
  type FormkitForm,
  type FormkitNode,
  type FormkitSection,
  ReferenceType,
} from "../../api";
import { Editor, Input, Message, RadioGroup } from "../components";
import { AccordionSection, Field, Form, Section } from "../layouts";
import {
  showIfKeyEqualsValue,
  showIfKeyIsFalsey,
  showIfKeyIsTruthy,
  showIfKeyNotEqualsValue,
  switchOnStateKey,
} from "../modifiers";
import {
  BomSection,
  concurrencySection,
  CsvHeaderSection,
  CsvQuoteStyleSection,
  DelimiterSection,
  FileModeSection,
  FilePathSection,
  FileTypeSection,
  MappingsSection,
  ParquetMappingsSection,
  RowDelimiterSection,
  SkipEmptySection,
  TimestampOffsetSection,
} from "../sections";

const liquidTemplateLanguage =
  "[Liquid template language](https://liquidjs.com/index.html)";

const batchNumberOption = {
  label: "Batch number",
  value: "batch",
  description:
    "Example: filename_1.{{ formState.fileType | default: 'csv' }} for the first batch.",
};

// Cannot support these options in multicontainer since we don't know how many total files will be created when reading the fragments
const monolithicNamingOptions = [
  {
    label: "Row range",
    value: "range",
    description:
      "Example: `filename_1-100.{{ formState.fileType | default: 'csv' }}` for the first 100 rows.",
  },
  {
    label: "Batch N of N",
    value: "n_of_n",
    description:
      "Example: `filename_batch-1-of-500.{{ formState.fileType | default: 'csv' }}` for the first batch of 500 total batches.",
  },
];

const singleUploadBatchFileNamingSection = showIfKeyIsFalsey({
  referenceType: ReferenceType.Context,
  key: "featureFlags.s3MulticontainerBatching",
  children: [
    Section({
      heading:
        "How would you like to name the sequential files created via batching?",
      subheading: "This value will be added prior to the file suffix.",
      children: [
        RadioGroup("batchingFilenameDetails", {
          options: [batchNumberOption],
          default: "batch",
        }),
      ],
    }),
  ],
});

const multipartBatchFileNamingSection = (
  multicontainerSupported: boolean | undefined,
) =>
  showIfKeyIsTruthy({
    referenceType: ReferenceType.Context,
    key: "featureFlags.s3MulticontainerBatching",
    children: [
      Section({
        heading:
          "How would you like to name the sequential files created via batching?",
        subheading: "This value will be added prior to the file suffix.",
        children: [
          RadioGroup("batchingFilenameDetails", {
            options: multicontainerSupported
              ? [batchNumberOption]
              : [batchNumberOption, ...monolithicNamingOptions],
            default: "batch",
          }),
        ],
      }),
    ],
  });

export const batchingSection = (
  multicontainerSupported: boolean | undefined,
) => {
  return [
    Section({
      heading: "Would you like to enable file batching?",
      subheading:
        "Batching may send more than 1 file to {{ destinationDefinition.name | default: 'your destination' }} based on the number of rows queried from your model. This may help avoid file size errors with {{ destinationDefinition.name | default: 'your destination' }}.",
      children: [
        RadioGroup("enableBatching", {
          options: [
            {
              label: "No",
              value: undefined,
            },
            {
              label: "Yes",
              value: true,
            },
          ],
        }),
      ],
    }),
    showIfKeyIsTruthy({
      key: "enableBatching",
      children: [
        Section({
          heading:
            "What is the maximum number of rows to be sent in each file?",
          subheading: `This is the maximum number of rows each output file will have. ${multicontainerSupported ? "Files may contain fewer rows than this number." : "If your model has more rows than the provided number, Hightouch will split the model's query results into multiple files."} Defaults to 10,000.`,
          children: [
            Input("batchSize", {
              type: "number",
              validation: Yup.number().positive(),
              placeholder: "10000",
            }),
          ],
        }),
        singleUploadBatchFileNamingSection,
        multipartBatchFileNamingSection(multicontainerSupported),
        Section({
          heading:
            "Where should the batch identifier be placed in the filename?",
          subheading:
            "This controls where the batch number appears when you have multiple file extensions.",
          optional: true,
          children: [
            RadioGroup("batchIdentifierPlacement", {
              default: "before_last_extension",
              options: [
                {
                  label: "Before last extension",
                  value: "before_last_extension",
                  description: "Example: filename.csv_1.gz",
                },
                {
                  label: "Before first extension",
                  value: "before_first_extension",
                  description: "Example: filename_1.csv.gz",
                },
              ],
            }),
          ],
        }),
      ],
    }),
  ];
};

const compressionSection = () =>
  Section({
    heading: "Would you like to compress your output files?",
    subheading:
      "Choose a compression format for your output files. Adding the matching " +
      "extension (`.gz` or `.zip`) to your file name is recommended, though not required.",
    children: [
      RadioGroup("compressionFormat", {
        // No default: an untouched sync must keep compressionFormat unset so the
        // execution boundary can preserve at-rest encryption for legacy
        // zip-password syncs (getEffectiveCompressionFormat). A written "none"
        // default would persist on save and silently disable that encryption.
        disableDefault: true,
        options: [
          {
            label: "None",
            value: "none",
          },
          {
            label: "Gzip",
            value: "gzip",
          },
          {
            label: "Zip",
            value: "zip",
          },
        ],
      }),
      showIfKeyEqualsValue({
        key: "compressionFormat",
        value: "zip",
        children: [
          Message({
            variant: "info",
            title: "Password protection",
            message:
              "Zip files can be encrypted at rest with AES-256. Set a zip password in the destination configuration and it is applied automatically whenever a sync uses Zip compression; leave it blank for a plain zip.",
          }),
        ],
      }),
    ],
  });

const indicatorFileNameSection = Section({
  heading:
    "Would you like to write an empty file after all files have been written to indicate completion?",
  subheading: `If left empty, no indicator file will be written. You can use the same template variables available for the file name. 
            An indicator file will not be written in the case of a sync failure or if the query result is empty.`,
  optional: true,
  children: [
    Input("indicatorFileName", {
      placeholder: "Enter a filename...",
      validation: Yup.string().nullable().notRequired(),
    }),
  ],
});

const fileIndicatorSection = () =>
  switchOnStateKey("mode", [
    {
      value: "all",
      children: [
        showIfKeyIsTruthy({
          key: "enableBatching",
          children: [indicatorFileNameSection],
        }),
      ],
    },
    {
      value: "insert",
      children: [
        showIfKeyIsTruthy({
          key: "enableBatching",
          children: [indicatorFileNameSection],
        }),
      ],
    },
    {
      value: "diff",
      children: [indicatorFileNameSection],
    },
  ]);

const rowDelimiterSection = RowDelimiterSection();
const delimiterSection = (props) => DelimiterSection(props.delimiterOptions);
const csvHeaderSection = (props) => CsvHeaderSection(props.csvHeaderOptions);
const bomSection = BomSection();
const csvQuoteStyleSection = CsvQuoteStyleSection();
const csvFormatOptions = (props) =>
  showIfKeyEqualsValue({
    key: "fileType",
    value: "csv",
    children: [
      Section({
        heading: "CSV format options",
        children: [
          delimiterSection(props),
          rowDelimiterSection,
          csvHeaderSection(props),
          bomSection,
          csvQuoteStyleSection,
        ],
      }),
    ],
  });

const advancedConfigsHiddenByDefault = [
  "enableBatching",
  "hideSkipEmptySection",
  "enableCompressionSection",
  "writeIndicatorFile",
];
const advancedSection = (props: FileStorageFormProps) => {
  if (
    !Object.entries(props).some(
      ([prop, value]) =>
        advancedConfigsHiddenByDefault.includes(prop) && Boolean(value),
    ) &&
    props.disableCSVFormatOptions
  ) {
    return [];
  }
  return [
    AccordionSection({
      children: [
        ...(props.disableCSVFormatOptions ? [] : [csvFormatOptions(props)]),
        ...(props.enableBatching
          ? [
              showIfKeyNotEqualsValue({
                key: "mode",
                value: "upsert",
                children: batchingSection(props.multicontainerSupported),
              }),
            ]
          : []),
        ...(props.hideSkipEmptySection ? [] : [SkipEmptySection()]),
        ...(props.writeIndicatorFile ? [fileIndicatorSection()] : []),
        ...(props.enableCompressionSection ? [compressionSection()] : []),
        ...(props.concurrencySection
          ? props.concurrencySection === true
            ? [concurrencySection()]
            : [props.concurrencySection].flat()
          : []),
      ],
    }),
  ];
};

export const FILE_TYPES = {
  csv: "CSV",
  json: "JSON",
  ndjson: "NDJSON (JSON Lines)",
  parquet: "Parquet",
  xml: "XML",
} as const;

export type FileType = keyof typeof FILE_TYPES;

type FileStorageFormProps = {
  modeOptions?: { options?: any };
  idSections?: FormkitSection[];
  /**
   * Enter the supported fileTypes.
   */
  types: [FileType, ...FileType[]];
  directorySection?: FormkitSection[];
  filePathOptions?: {
    heading?: string;
    subheading?: string;
    fieldName?: string;
    fileType?: string;
  };
  timestampOptions?: {
    default?: any;
  };
  fileHandlingSections?: FormkitSection[];
  disableCSVFormatOptions?: boolean;
  delimiterOptions?: { options?: any };
  csvHeaderOptions?: { default?: boolean };
  disableMappings?: boolean;
  /**
   * Allows user to opt-in for batching.
   * Batching will sync the file into multiple smaller batch.
   * This is automatically handled by the framework.
   */
  enableBatching?: boolean;
  enableCompressionSection?: boolean;
  /**
   * If true, the form will not include the `skipEmpty` section.
   * Setting this to true also means that the syncs will by default send empty files.
   */
  hideSkipEmptySection?: boolean;

  writeIndicatorFile?: boolean;

  existingFileSection?: { key: string; children: FormkitNode[] };

  // Pass nodes to control visibility, for example swapping in a notice when the destination encrypts output.
  concurrencySection?: boolean | FormkitNode | FormkitNode[];

  multicontainerSupported?: boolean;

  enableInLineMapper?: boolean;
};

/**
 * FileStorage Formkit Generator.
 * @param props
 * @returns
 */
export function FileStorageFormGenerator(
  props: FileStorageFormProps,
): FormkitForm {
  const modeSection = FileModeSection(props?.modeOptions);
  const timestampOffsetSection = TimestampOffsetSection(
    props?.timestampOptions,
  );

  // `autoSyncColumnsDefault` is a static prop (no state-reference support), so
  // we render two variants gated by mode: upsert forces it to false since the
  // framework rejects auto-sync columns for upsert at runtime.
  const mappingsSectionForUpsert = (autoSyncColumnsDefault: boolean) =>
    MappingsSection({
      allEnabled: true,
      autoSyncColumnsDefault,
      ...(props?.enableInLineMapper
        ? { advanced: true, enableInLineMapper: true }
        : {}),
    });

  const mappings = (fileType: FileType) => {
    if (props?.disableMappings) return [];
    if (fileType === "parquet") return [ParquetMappingsSection()];

    return [
      showIfKeyEqualsValue({
        key: "mode",
        value: "upsert",
        children: [mappingsSectionForUpsert(false)],
      }),
      showIfKeyNotEqualsValue({
        key: "mode",
        value: "upsert",
        children: [mappingsSectionForUpsert(true)],
      }),
    ];
  };

  const directorySection = props?.directorySection ?? [];
  const jsonSection = [
    ...directorySection,
    FilePathSection({
      ...(props?.filePathOptions ?? {}),
      fileType: "json",
    }),
    timestampOffsetSection,
    ...(props?.fileHandlingSections ?? []),
    ...mappings("json"),
  ];
  const ndjsonSection = [
    ...directorySection,
    FilePathSection({
      ...(props?.filePathOptions ?? {}),
      fileType: "ndjson",
    }),
    timestampOffsetSection,
    ...(props?.fileHandlingSections ?? []),
    ...mappings("ndjson"),
  ];

  const csvSection = [
    ...directorySection,
    FilePathSection({
      ...(props?.filePathOptions ?? {}),
      fileType: "csv",
    }),
    timestampOffsetSection,
    ...(props?.fileHandlingSections ?? []),
    ...mappings("csv"),
  ];

  const parquetSection = [
    ...directorySection,
    FilePathSection({
      ...(props?.filePathOptions ?? {}),
      fileType: "parquet",
    }),
    timestampOffsetSection,
    ...(props?.fileHandlingSections ?? []),
    ...mappings("parquet"),
  ];
  const xmlSection = [
    ...directorySection,
    FilePathSection({
      ...(props?.filePathOptions ?? {}),
      fileType: "xml",
    }),
    timestampOffsetSection,
    ...(props?.fileHandlingSections ?? []),
    Section({
      heading: "Define your XML payload",
      subheading: `Use the text area below to construct your XML payload. Hightouch supports the ${liquidTemplateLanguage} for injecting variable, control flows, loops, and more.`,
      ...props,
      children: [
        Field({
          heading: "XML header",
          component: Editor("xmlHeader", {
            placeholder: "<CreateCustomerList>",
            validation: Yup.string().required(),
            language: "xml",
            languageAsLiquidBase: true,
          }),
        }),
        Field({
          heading: "XML body",
          subheading:
            "This template is rendered for each row and then combined together.",
          component: Editor("xmlBody", {
            placeholder: `<CreateCustomer>
    <Name>{{ row.full_name }}</Name>
    <Location>{{ row.location }}</Location>
</CreateCustomer>`,
            validation: Yup.string().required(),
            language: "xml",
            languageAsLiquidBase: true,
          }),
        }),
        Field({
          heading: "XML footer",
          component: Editor("xmlFooter", {
            placeholder: "</CreateCustomerList>",
            validation: Yup.string().required(),
            language: "xml",
            languageAsLiquidBase: true,
          }),
        }),
      ],
    }),
  ];

  const sections: { [key in FileType]: FormkitNode[] } = {
    json: jsonSection,
    ndjson: ndjsonSection,
    csv: csvSection,
    parquet: parquetSection,
    xml: xmlSection,
  };

  const fileTypes = props.types;
  const typeSection = FileTypeSection({
    options: fileTypes.map((type) => ({
      label: FILE_TYPES[type],
      value: type,
    })),
  });
  // Only include the FileType section if multiple file type is supported.
  const fileTypeSection = fileTypes.length > 1 ? [typeSection] : [];
  const fileBuildingSections = fileTypes.map((type) => {
    return {
      key: type,
      section: sections[type],
    };
  });

  let mainSection;
  if (fileTypes.length > 1) {
    mainSection = [
      switchOnStateKey(
        "fileType",
        fileBuildingSections.map((section) => {
          return {
            value: section.key,
            children: section.section,
          };
        }),
      ),
    ];
  } else if (fileTypes.includes("csv")) {
    mainSection = csvSection;
  } else if (fileTypes.includes("json")) {
    mainSection = jsonSection;
  } else if (fileTypes.includes("parquet")) {
    mainSection = parquetSection;
  } else if (fileTypes.includes("ndjson")) {
    mainSection = ndjsonSection;
  }

  const fileCreationSection = props?.existingFileSection?.key
    ? [
        Section({
          heading: "Would you like to sync to an existing file?",
          children: [
            RadioGroup(props.existingFileSection.key, {
              default: false,
              options: [
                {
                  label: "No",
                  value: false,
                },
                {
                  label: "Yes",
                  value: true,
                },
              ],
            }),
          ],
        }),
        switchOnStateKey(props.existingFileSection.key, [
          {
            value: true,
            children: [
              ...(props?.existingFileSection?.children || []),
              ...fileTypeSection,
            ],
          },
          { value: false, children: [...fileTypeSection, ...mainSection] },
        ]),
      ]
    : [...fileTypeSection, ...mainSection];

  return Form({
    children: [
      modeSection,
      ...(props?.idSections ?? []),
      ...fileCreationSection,
      ...advancedSection(props),
    ],
  });
}
